import { Dayjs } from 'dayjs';
import { BusinessModuleRef } from '../business-module-ref.js';
import { SerialNumberPool, SerialNumberPricingContext } from './serial-number-pool.js';
import { EventTicket } from '../event-ticket.js';
import { getTicketTypeId } from '../ticket-type.js';
import { SerialNumberScope } from './serial-number-scope.js';
import { IProductWithSerialNumber } from '../../../contracts/product-with-serial-number.js';
import { ErrorLike } from '../../core/error-like.js';
import { Result } from '../../../utility/result.js';
import { SerialNumberStrategy } from './serial-number-strategy.js';
import { OpenAccessPass } from '../open-access-pass/open-access-pass.js';
import { UnsupportedProductType } from './unsupported-product-type.js';
import { TicketGiftCard } from '../discounts/ticket-gift-card.js';
import { OpenTicketPass } from '../discounts/open-ticket-pass.js';
import { GiftCard } from '../gift-card/gift-card.js';

// NOTE: Some utilities are only implemented in Dart. Port them if necessary.
export abstract class StandardSerialNumberPool extends SerialNumberPool {
  public abstract get businessRef(): BusinessModuleRef;
  public abstract get eventId(): string | null;
  public abstract get creationDate(): Dayjs;
  public abstract get shortCode(): string;
  public abstract get scope(): SerialNumberScope;

  protected abstract get serialNumberBuckets(): Map<string, number>;

  abstract get strategy(): SerialNumberStrategy;

  getStrategy(): SerialNumberStrategy {
    return this.strategy;
  }

  abstract resetForNewSeason(seasonId: string): this;

  protected getFormattedProductTypeId(
      product: IProductWithSerialNumber,
  ): Result<string, ErrorLike> {
    switch(true) {
      case product instanceof EventTicket: {
        return Result.ok(getTicketTypeId(product.ticketType).toFixed().padStart(2, '0'));
      } break;
      case product instanceof OpenAccessPass: {
        return Result.ok('P');
      } break;
      case product instanceof TicketGiftCard: {
        return Result.ok('V');
      } break;
      case product instanceof GiftCard: {
        return Result.ok('G');
      } break;
      case product instanceof OpenTicketPass: {
        return Result.ok('V');
      } break;
      default: {
        return Result.error(new UnsupportedProductType(product));
      } break;
    }
  }

  protected abstract getFormattedSeasonId(
      product: IProductWithSerialNumber,
  ): Result<string | null, ErrorLike>;

  protected abstract getFormattedProductGroupId(
    product: IProductWithSerialNumber,
    pricingContext?: SerialNumberPricingContext,
  ): Result<string, ErrorLike>;

  protected abstract getProductSerialNumberBucketId(
      product: IProductWithSerialNumber,
      pricingContext?: SerialNumberPricingContext,
  ): Result<string, ErrorLike>;

  getSerialNumber(product: IProductWithSerialNumber, pricingContext?: SerialNumberPricingContext): Result<string, ErrorLike> {
    return Result.concat([
      this.getFormattedProductTypeId(product),
      this.getFormattedSeasonId(product),
      this.getFormattedProductGroupId(product, pricingContext),
      this.getProductSerialNumberBucketId(product, pricingContext),
    ]).map(
        ([typeId, seasonId, groupId, bucketId]) => {
          const serialNumber = this.getNextSerialNumber(bucketId);
          this.markSerialNumberAsUsed(bucketId, serialNumber);

          const chunks: string[] = [
            this.shortCode,
            typeId,
            groupId,
            serialNumber.toFixed().padStart(4, '0'),
          ];

          if(seasonId != null) {
            chunks.splice(2, 0, seasonId);
          }

          return chunks.join(SerialNumberPool.DELIMITER);
        });
  }

  private getNextSerialNumber(bucketId: string): number {
    return (this.serialNumberBuckets.get(bucketId) ?? 0) + 1;
  }

  private markSerialNumberAsUsed(bucketId: string, serialNumber: number): void {
    this.serialNumberBuckets.set(bucketId, serialNumber);
  }
}
